import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { WarehouseIcon, ArrowLeftRightIcon, DoorOpenIcon, TruckIcon, ZapIcon, MapPinIcon } from 'lucide-react';
interface FeatureCardProps {
  icon: React.ReactNode;
  value: string;
  label: string;
  index: number;
}
function FeatureCard({
  icon,
  value,
  label,
  index
}: FeatureCardProps) {
  return <motion.div className="flex flex-col items-center rounded-lg border border-white/10 bg-dark-light px-6 py-10 hover:border-accent/50 transition-colors duration-300" variants={{
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: index * 0.08,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  }} data-id="element-47">
      <div className="mb-5 text-accent" aria-hidden="true" data-id="element-48">
        {icon}
      </div>
      <p className="font-heading text-2xl md:text-3xl font-bold text-white mb-2 text-center" data-id="element-49">
        {value}
      </p>
      <p className="font-body text-sm text-white/50 tracking-wide text-center" data-id="element-50">
        {label}
      </p>
    </motion.div>;
}
export function FeaturesSection() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section ref={ref} className="w-full bg-dark px-6 py-20 md:py-26" aria-label="Property features" data-id="element-51">
      <div className="mx-auto max-w-5xl" data-id="element-52">
        <motion.div className="mb-12" initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }} data-id="element-53">
          <p className="font-heading text-xs font-bold tracking-widest-plus uppercase text-accent mb-4" data-id="element-54">
            Key Specifications
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white" data-id="element-55">
            Property Particulars
          </h2>
        </motion.div>

        <motion.div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.08
          }
        }
      }} data-id="element-56">
          <FeatureCard icon={<WarehouseIcon size={32} strokeWidth={1.5} data-id="element-58" />} value="355 sqm*" label="Building Area" index={0} data-id="element-57" />
          <FeatureCard icon={<ArrowLeftRightIcon size={32} strokeWidth={1.5} data-id="element-60" />} value="14 m*" label="Street Frontage" index={1} data-id="element-59" />
          <FeatureCard icon={<DoorOpenIcon size={32} strokeWidth={1.5} data-id="element-62" />} value="4 m*" label="Roller Door Height" index={2} data-id="element-61" />
          <FeatureCard icon={<TruckIcon size={32} strokeWidth={1.5} data-id="element-64" />} value="Rear Access" label="Logistics / Loading" index={3} data-id="element-63" />
          <FeatureCard icon={<ZapIcon size={32} strokeWidth={1.5} data-id="element-66" />} value="Available" label="3-Phase Power" index={4} data-id="element-65" />
          <FeatureCard icon={<MapPinIcon size={32} strokeWidth={1.5} data-id="element-68" />} value="1 km*" label="To Melbourne CBD" index={5} data-id="element-67" />
        </motion.div>
      </div>
    </section>;
}